import React, { lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import '../styles/home.css';

// Lazy load heavy components for better initial load time
const ProductShowcase = lazy(() => import('../components/ProductShowcase'));
const BlogSection = lazy(() => import('../components/BlogSection'));
const WhyChooseUs = lazy(() => import('../components/WhyChooseUs'));
const BrandShowcase = lazy(() => import('../components/ui/BrandShowcase').then(module => ({ default: module.BrandShowcase })));
const WovenLightHero = lazy(() =>
  import('../components/ui/woven-light-hero').then(module => ({
    default: module.WovenLightHero
  }))
);

const EdgeFeatures = lazy(() => import('../components/ui/EdgeFeatures'));

// Loading fallback component
const SectionLoader = ({ height = '100vh', bg = '#000' }) => (
  <div style={{
    minHeight: height,
    background: bg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '3px solid rgba(255, 255, 255, 0.1)',
      borderTop: '3px solid #fff',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
  </div>
);

export default function Home({ onCategoryClick, onViewProducts, onProductClick }) {
  return (
    <div className="home-page">
      <Helmet>
        <title>Sitcorp International - Premium Sourcing & Manufacturing Solutions</title>
        <meta name="description" content="Leading global manufacturer and sourcing partner specializing in apparel, textiles, home décor, and accessories. Sustainable, ethical manufacturing with 15+ years of expertise serving major retailers worldwide." />
        <meta name="keywords" content="sustainable manufacturing, ethical sourcing, apparel manufacturer, textile production, home décor supplier, quality control, global sourcing" />
        <link rel="canonical" href="https://sitcorpinternational.com/" />
        <meta property="og:title" content="Sitcorp International - Premium Sourcing & Manufacturing Solutions" />
        <meta property="og:description" content="Leading global manufacturer and sourcing partner specializing in apparel, textiles, home décor, and accessories." />
        <meta property="og:url" content="https://sitcorpinternational.com/" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* 3D Globe Hero Section */}
      <div id="hero">
        <Suspense fallback={<div style={{ minHeight: '100vh', background: '#000' }} />}>
          <WovenLightHero onPrimaryClick={onViewProducts} />
        </Suspense>
      </div>

      <section className="mission" id="mission">
        <div className="container">
          <div className="mission-layout">
            <div className="mission-parallax">
              <img 
                src={`${process.env.PUBLIC_URL}/logo.png`}
                alt="Sitcorp International Logo"
                className="mission-logo"
              />
            </div>
            <div className="mission-content">
              <h2>Our Mission</h2>
              <p>At Sitcorp International, our mission is to deliver high-quality, sustainable sourcing and manufacturing solutions that empower brands and retailers around the globe. We combine ethical sourcing, responsible material selection, and advanced, eco-conscious manufacturing to bring superior products to market while protecting natural resources for future generations.</p>
              <p>Working as a trusted partner from concept through delivery, we provide tailored product development, efficient manufacturing, rigorous quality control, and dependable order execution. Our focus on innovation, sustainability, and competitive pricing helps brands scale responsibly and reliably across global markets.</p>
            </div>
          </div>
        </div>
      </section>



      <div id="capabilities" className="premium-panel premium-block" style={{ '--section-bg': `url(${process.env.PUBLIC_URL + '/assets/OurEdge.png'})` }}>
        <Suspense fallback={<SectionLoader height="100vh" bg="#000" />}>
          <EdgeFeatures />
        </Suspense>
      </div>

      

      <div id="products" className="premium-panel premium-block">
        <Suspense fallback={<div style={{background: '#f5f5f5' }} />}>
          <ProductShowcase onViewMore={onViewProducts} onProductClick={onProductClick} />
        </Suspense>
      </div>
      

      <div id="why-choose-us" className="premium-panel premium-block">
        <Suspense fallback={<div/>}>
          <WhyChooseUs />
        </Suspense>
      </div>

            <div className="premium-panel premium-block premium-clients">
        <Suspense fallback={<div style={{  background: '#000' }} />}>
          <BrandShowcase customers={[
            { name: 'Michaels', url: `${process.env.PUBLIC_URL}/assets/Clients/Michaels.png`, height: 60 },
            { name: 'Walmart', url: `${process.env.PUBLIC_URL}/assets/Clients/Walmart.png`, height: 60 },
            { name: 'Dollar General', url: `${process.env.PUBLIC_URL}/assets/Clients/Dollar General.png`, height: 60 },
            { name: 'Target', url: `${process.env.PUBLIC_URL}/assets/Clients/Target.png`, height: 60 },
            { name: 'Carven', url: `${process.env.PUBLIC_URL}/assets/Clients/Carven.png`, height: 60 },
            { name: 'Long Beach Polo Club', url: `${process.env.PUBLIC_URL}/assets/Clients/Long Beach Polo Club.jpeg`, height: 60 },
            { name: 'Cubavera', url: `${process.env.PUBLIC_URL}/assets/Clients/Cubavera.jpeg`, height: 60 },
            { name: 'Brums', url: `${process.env.PUBLIC_URL}/assets/Clients/Brums.png`, height: 60 },
            { name: 'Dollar Tree', url: `${process.env.PUBLIC_URL}/assets/Clients/Dollar Tree.png`, height: 60 },
            { name: 'Ross Stores', url: `${process.env.PUBLIC_URL}/assets/Clients/Ross Stores.png`, height: 60 },
            { name: 'Burlington', url: `${process.env.PUBLIC_URL}/assets/Clients/Burlington.png`, height: 60 },
          ]} />
        </Suspense>
      </div>

      <div className="premium-panel premium-block">
        <Suspense fallback={<div style={{ minHeight: '400px', background: '#fff' }} />}>
          <BlogSection />
        </Suspense>
      </div>


    </div>
  );
}
